import type { ProviderFieldSchema } from '@/domain/providerConfig';

const SIGNAL_UNIT_PREFIX = 'extra_config.signal_units.';

/** Spellings the fetch reads the same way (signal-folding.ts), shown under one name. */
const UNIT_ALIASES: Readonly<Record<string, string>> = {
    celsius: 'c',
    fahrenheit: 'f',
    '%': 'pct',
    percent: 'pct',
};

export interface EnumChoice {
    /** What is stored, exactly as the schema lists it. */
    value: string;
    /** The copy for it, or null where the raw value is all there is to show. */
    labelKey: string | null;
}

/** The units with a name of their own, under providerConfig.units. */
export const SIGNAL_UNIT_LABELS: readonly string[] = [
    'g',
    'kg',
    't',
    'ratio',
    'c',
    'f',
    'pct',
    'fraction',
];
const UNIT_LABELS: ReadonlySet<string> = new Set(SIGNAL_UNIT_LABELS);

function unitOf(value: string): string {
    return UNIT_ALIASES[value] ?? value;
}

/**
 * The options of an enum field. A signal unit shows its name, once per meaning: an alias
 * appears only while it is the stored value, in place of the spelling it equals, so the
 * stored value still reads as selected and is saved back unchanged.
 */
export function enumChoices(field: ProviderFieldSchema, current: string): EnumChoice[] {
    const choices = listedChoices(field, current);
    // A stored value no longer offered (e.g. ratio on a weight) still shows as what is
    // stored, so the select does not claim another value; its note says it is not read.
    const kept = current === '' || choices.some((choice) => choice.value === current);
    return kept ? choices : [...choices, { value: current, labelKey: null }];
}

function listedChoices(field: ProviderFieldSchema, current: string): EnumChoice[] {
    if (!field.key.startsWith(SIGNAL_UNIT_PREFIX))
        return field.enumValues.map((value) => ({ value, labelKey: null }));
    const byUnit = new Map<string, string>();
    for (const value of field.enumValues) {
        const unit = unitOf(value);
        const isCurrent = value === current;
        if (!byUnit.has(unit) || isCurrent) byUnit.set(unit, value);
    }
    return [...byUnit].map(([unit, value]) => ({
        value,
        labelKey: UNIT_LABELS.has(unit) ? `providerConfig.units.${unit}` : null,
    }));
}

/** The copy key of a field's default when it is a unit with a name, else null. */
export function defaultLabelKey(field: ProviderFieldSchema): string | null {
    if (field.defaultValue === null) return null;
    const value = field.defaultValue;
    return enumChoices(field, value).find((choice) => choice.value === value)?.labelKey ?? null;
}

/**
 * What a box shows for `value`: an enum value read-only by its name, as its select would
 * show it, since the read-only box is plain text; anything else as it is.
 */
export function shownValue(
    field: ProviderFieldSchema,
    value: string,
    t: (key: string) => string,
    readOnly: boolean
): string {
    if (!readOnly || field.kind !== 'enum' || value === '') return value;
    const choice = enumChoices(field, value).find((entry) => entry.value === value);
    return choice?.labelKey ? t(choice.labelKey) : value;
}
